import * as React from "react";
import { Checkbox, FormControl, MenuItem, ListSubheader } from "@mui/material";
import { Field, useField } from "formik";
import { TextField } from "formik-mui";
import { useTranslation } from "react-i18next";
import { SelectOption, SelectOptionGroup } from "@mui/base";

interface Props {
  name: string;
  label?: string;
  options: SelectOptionGroup<string | number>[]; // Updated to SelectOptionGroup type
  required?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  helperText?: string;
  variant?: string;
  size?: string;
}

const SelectManyGroupOptionField: React.FC<Props> = ({
  name,
  label,
  options,
  required,
  disabled,
  isLoading,
  isError,
  helperText,
  variant,
  size
}) => {
  const { t } = useTranslation();
  const [field] = useField<string[]>({ name });

  return (
    <FormControl fullWidth>
      <Field
        component={TextField}
        type="text"
        name={name}
        label={label}
        helperText={helperText}
        placeholder={isLoading ? "Loading..." : (isError && "Error") || label}
        select
        variant={variant}
        disabled={disabled || isLoading || isError}
        SelectProps={{
          multiple: true,
          renderValue: (selected: string[]) => {
            const selectedLabels = selected
              .map(
                value =>
                  options
                    .flatMap(group => group.options)
                    .find(option => (option as SelectOption<string>).value === value)?.label
              )
              .filter(Boolean);
            return selectedLabels.join(", ");
          },
          MenuProps: {
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left"
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left"
            },
            getContentAnchorEl: null
          }
        }}
        required={required}
        validate={(value: string[] | undefined) =>
          required && (!value || value.length === 0) ? t("common.validation.required") : undefined
        }
        size={size}
      >
        {options.map(group => [
          <ListSubheader color="default" key={`subheader-${group.label}`}>
            {group.label?.toString().toLocaleUpperCase()}
          </ListSubheader>,
          ...group.options.map(option => (
            <MenuItem
              key={(option as SelectOption<string>).value}
              value={(option as SelectOption<string>).value}
              disabled={option.disabled}
            >
              <Checkbox
                checked={field.value.includes(String((option as SelectOption<string>).value))}
                disabled={option.disabled}
              />
              {option.label}
            </MenuItem>
          ))
        ])}
      </Field>
    </FormControl>
  );
};

export { SelectManyGroupOptionField };
